import { MaskEvent } from '@react-input/mask';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import Toast from 'react-bootstrap/Toast';
import {
  CaptionDropdowns,
  CaptionProps,
  DateRange,
  DayPicker,
  DayPickerProps,
  useNavigation,
} from 'react-day-picker';
import { useForm } from 'react-hook-form';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import { FormInputMaskField } from '../FormInputMaskField';
import { addDayToRange } from './utils';
import './DateRangePiker.scss';

const FROM_YEAR = 2022;

const CustomCaptionComponent = (props: CaptionProps) => {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();

  return (
    <div className="d-flex align-items-center justify-content-between mb-2">
      <BsChevronLeft
        style={{ cursor: 'pointer' }}
        size={16}
        onClick={() => previousMonth && goToMonth(previousMonth)}
      />
      <CaptionDropdowns {...props} />
      <BsChevronRight
        size={16}
        style={{ cursor: 'pointer' }}
        onClick={() => nextMonth && goToMonth(nextMonth)}
      />
    </div>
  );
};

export const DateRangePiker = ({
  onChange,
  value = { from: '', to: '' },
  dateFormat = 'MM.dd.yyyy',
  disabled,
  compact = false,
  mask = '__.__.____',
  xs = 6,
  sm = 6,
  validateRange,
  placeholder,
}: {
  onChange: (range: { from: string; to: string }) => void;
  validateRange?: (range: { from: string; to: string }) => {
    from: string | null;
    to: string | null;
  };
  value: {
    from: string;
    to: string;
  };
  dateFormat?: string;
  disabled?: Date[];
  compact?: boolean;
  mask?: string;
  xs?: number;
  sm?: number;
  placeholder?: string;
}) => {
  const [range, setRange] = useState<DateRange | null>(null);
  const [isFromCalendarOpened, setIsFromCalendarOpened] = useState<boolean>(false);
  const [isToCalendarOpened, setIsToCalendarOpened] = useState<boolean>(false);
  const { register, setValue, reset, getValues, clearErrors, formState, setError } = useForm({
    defaultValues: { from: value?.from, to: value?.to },
  });
  const formErrors = formState.errors;
  const [displayedMonth, setDisplayedMonth] = useState(new Date());
  const TO_YEAR = useMemo(() => new Date().getFullYear() + 5, []);

  useEffect(() => {
    if (value && value.from && value.to) {
      setRange({
        from: new Date(value.from),
        to: new Date(value.to),
      });

      setDisplayedMonth(new Date(value.from));

      return;
    }

    reset(value);
    setRange(null);
  }, [value]);

  const handleError = ({
    from: fromError,
    to: toError,
  }: {
    from: string | null;
    to: string | null;
  }) => {
    fromError && setError('from', { type: 'manual', message: fromError });
    toError && setError('to', { type: 'manual', message: toError });
  };

  const handleDayClick: DayPickerProps['onDayClick'] = (day, modifiers) => {
    clearErrors();
    if (modifiers.selected) {
      setRange(null);
      reset({
        from: '',
        to: '',
      });

      onChange({
        from: '',
        to: '',
      });

      return;
    }

    const newRange = addDayToRange(day, range);

    if (newRange.from && !isNaN(newRange.from.getTime())) {
      const from = format(newRange.from, dateFormat);
      setValue('from', from);
    }

    if (newRange.to && !isNaN(newRange.to.getTime())) {
      const to = format(newRange.to, dateFormat);
      setValue('to', to);
    }

    value = getValues();
    onChange(value);
    validateRange && handleError(validateRange(value));
    return setRange(newRange);
  };

  const onChangeInput = (event: MaskEvent, key: keyof DateRange) => {
    clearErrors();
    const {
      detail: { value, isValid },
    } = event;

    if (!isValid) return;

    const date = new Date(value);

    if (isNaN(date.getTime())) return;

    if (date > range?.to && key === 'from') {
      setRange({
        from: date,
        to: date,
      });

      setValue('to', value);

      onChange({
        from: getValues().from,
        to: value,
      });
      handleError(
        validateRange({
          from: getValues().from,
          to: value,
        })
      );

      return;
    }

    if (date < range?.from && key === 'to') {
      setRange({
        from: date,
        to: date,
      });

      setValue('from', value);

      onChange({
        from: value,
        to: value,
      });

      handleError(
        validateRange({
          from: value,
          to: value,
        })
      );

      return;
    }

    onChange({
      ...getValues(),
      [key]: value,
    });

    setRange({
      ...range,
      [key]: date,
    } as DateRange);
  };

  const onToggleFrom = () => {
    setIsFromCalendarOpened((value) => !value);
  };

  const onToggleTo = () => {
    setIsToCalendarOpened((value) => !value);
  };

  return (
    <Row>
      <Col xs={xs} sm={sm} className="position-relative">
        <FormInputMaskField
          isCalendarShownInCompactMode={isFromCalendarOpened}
          onToggleCalendar={onToggleFrom}
          compact={compact}
          hideLabel
          label="From"
          name="from"
          register={register}
          mask={mask}
          replacement={{ _: /\d/ }}
          placeholder={placeholder || dateFormat}
          onMask={(event) => onChangeInput(event, 'from')}
          errors={formErrors}
        />
        {compact ? (
          <Toast
            show={isFromCalendarOpened}
            // onClose={() => setIsFromCalendarOpened(false)}
            className="custom-date-picker-toast left"
          >
            <DayPicker
              disabled={disabled}
              className="date-rage-piker compact"
              fromYear={FROM_YEAR}
              toYear={TO_YEAR}
              mode="range"
              weekStartsOn={0}
              selected={range as DateRange}
              components={{
                Caption: CustomCaptionComponent,
              }}
              onDayClick={handleDayClick}
            />
          </Toast>
        ) : null}
      </Col>
      <Col xs={xs} sm={sm} className="position-relative">
        <FormInputMaskField
          isCalendarShownInCompactMode={isToCalendarOpened}
          compact={compact}
          onToggleCalendar={onToggleTo}
          hideLabel
          label="To"
          name="to"
          register={register}
          mask={mask}
          replacement={{ _: /\d/ }}
          placeholder={placeholder || dateFormat}
          onMask={(event) => onChangeInput(event, 'to')}
          errors={formErrors}
        />
        {compact ? (
          <Toast
            show={isToCalendarOpened}
            // onClose={() => setIsToCalendarOpened(false)}
            className="custom-date-picker-toast right"
          >
            <DayPicker
              disabled={disabled}
              className="date-rage-piker compact"
              fromYear={FROM_YEAR}
              toYear={TO_YEAR}
              mode="range"
              weekStartsOn={0}
              selected={range as DateRange}
              components={{
                Caption: CustomCaptionComponent,
              }}
              onDayClick={handleDayClick}
            />
          </Toast>
        ) : null}
      </Col>
      {!compact ? (
        <Col xs={12} className="mt-3">
          <DayPicker
            disabled={disabled}
            className="date-rage-piker"
            fromYear={FROM_YEAR}
            toYear={TO_YEAR}
            mode="range"
            weekStartsOn={0}
            selected={range as DateRange}
            components={{
              Caption: CustomCaptionComponent,
            }}
            onDayClick={handleDayClick}
          />
        </Col>
      ) : null}
    </Row>
  );
};
